import React, { useState } from 'react';
import { Star } from 'lucide-react';

interface SurveyData {
  age_group: string;
  gender: string;
  party_size: string;
  visit_purpose: string;
  time_period: string;
  food_rating: number;
  good_dishes: string;
  service_rating: number;
  service_notes: string;
  atmosphere_rating: number;
  atmosphere_notes: string;
  budget_per_person: string;
  general_feedback: string;
}

interface SurveyFormProps {
  onSubmit: (data: SurveyData) => void;
  isSubmitting: boolean;
  shopName: string;
  settings: SurveySettings;
}

interface SurveySettings {
  enabled: boolean;
  welcome_message: string;
  thanks_message: string;
  email_required: boolean;
  age_group_required: boolean;
  visit_frequency_required: boolean;
  gender_required: boolean;
}

const StarRating: React.FC<{
  rating: number;
  onRatingChange: (rating: number) => void;
  label: string;
  required?: boolean;
}> = ({ rating, onRatingChange, label, required = false }) => {
  return (
    <div className="mb-6">
      <div className="mb-3">
        <label className="text-lg font-semibold text-gray-800">
          {label}
          {required && <span className="text-red-500 ml-2">*</span>}
        </label>
      </div>
      <div className="flex justify-center space-x-1 sm:space-x-2 mb-3">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onRatingChange(star)}
            className={`p-1 sm:p-2 transition-all duration-200 transform hover:scale-110 ${
              star <= rating 
                ? 'text-amber-400 drop-shadow-lg' 
                : 'text-gray-300 hover:text-amber-300'
            }`}
          >
            <Star 
              size={32} 
              className={`sm:w-10 sm:h-10 filter drop-shadow-sm ${
                star <= rating ? 'fill-current' : 'fill-none'
              }`}
            />
          </button>
        ))}
      </div>
      <div className="text-center">
        <p className={`text-sm font-medium ${
          rating === 0 ? 'text-gray-500' : 'text-amber-600'
        }`}>
          {rating === 0 ? 'クリックして評価してください' : `${rating}点 / 5点満点`}
        </p>
      </div>
    </div>
  );
};

const SurveyForm: React.FC<SurveyFormProps> = ({ onSubmit, isSubmitting, shopName, settings }) => {
  const [formData, setFormData] = useState<SurveyData>({
    age_group: '',
    gender: '',
    party_size: '',
    visit_purpose: '',
    time_period: '',
    food_rating: 0,
    good_dishes: '',
    service_rating: 0,
    service_notes: '',
    atmosphere_rating: 0,
    atmosphere_notes: '',
    budget_per_person: '',
    general_feedback: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // 必須項目のバリデーション
    if (!formData.age_group) newErrors.age_group = '年代を選択してください';
    if (!formData.party_size) newErrors.party_size = '利用人数を選択してください';
    if (!formData.visit_purpose) newErrors.visit_purpose = '利用用途を選択してください';
    if (!formData.time_period) newErrors.time_period = '利用時間帯を選択してください';
    if (formData.food_rating === 0) newErrors.food_rating = '料理の評価を選択してください';
    if (formData.service_rating === 0) newErrors.service_rating = '接客の評価を選択してください';
    if (formData.atmosphere_rating === 0) newErrors.atmosphere_rating = '雰囲気の評価を選択してください';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const updateField = (field: keyof SurveyData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-8">
        
        {/* 基本情報セクション */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg border-2 border-blue-200">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">基本情報</h2>
            <p className="text-gray-600">ご利用状況について教えてください</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* 年代 */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md border border-gray-200">
              <label className="text-lg font-semibold text-gray-800 mb-4 block">
                年代
                <span className="text-red-500 ml-2">*</span>
              </label>
              <select
                value={formData.age_group}
                onChange={(e) => updateField('age_group', e.target.value)}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-200 ${
                  errors.age_group 
                    ? 'border-red-300 focus:border-red-500 bg-red-50' 
                    : 'border-gray-200 focus:border-blue-400 hover:border-blue-300'
                }`}
                required
              >
                <option value="">選択してください</option>
                <option value="10代">10代</option>
                <option value="20代">20代</option>
                <option value="30代">30代</option>
                <option value="40代">40代</option>
                <option value="50代">50代</option>
                <option value="60代以上">60代以上</option>
              </select>
              {errors.age_group && (
                <p className="text-red-500 text-sm mt-2 text-center">{errors.age_group}</p>
              )}
            </div>

            {/* 性別 */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md border border-gray-200">
              <label className="text-lg font-semibold text-gray-800 mb-4 block">
                性別
                <span className="text-sm text-gray-500 ml-2">(任意)</span>
              </label>
              <select
                value={formData.gender}
                onChange={(e) => updateField('gender', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-400 hover:border-blue-300 transition-all duration-200"
              >
                <option value="">選択してください</option>
                <option value="男性">男性</option>
                <option value="女性">女性</option>
                <option value="その他">その他</option>
              </select>
            </div>

            {/* 利用人数 */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md border border-gray-200">
              <label className="text-lg font-semibold text-gray-800 mb-4 block">
                利用人数
                <span className="text-red-500 ml-2">*</span>
              </label>
              <select
                value={formData.party_size}
                onChange={(e) => updateField('party_size', e.target.value)}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-200 ${
                  errors.party_size 
                    ? 'border-red-300 focus:border-red-500 bg-red-50' 
                    : 'border-gray-200 focus:border-blue-400 hover:border-blue-300'
                }`}
                required
              >
                <option value="">選択してください</option>
                <option value="1人">1人</option>
                <option value="2人">2人</option>
                <option value="3-4人">3-4人</option>
                <option value="5-6人">5-6人</option>
                <option value="7人以上">7人以上</option>
              </select>
              {errors.party_size && (
                <p className="text-red-500 text-sm mt-2 text-center">{errors.party_size}</p>
              )}
            </div>

            {/* 利用用途 */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md border border-gray-200">
              <label className="text-lg font-semibold text-gray-800 mb-4 block">
                利用用途
                <span className="text-red-500 ml-2">*</span>
              </label>
              <select
                value={formData.visit_purpose}
                onChange={(e) => updateField('visit_purpose', e.target.value)}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-200 ${
                  errors.visit_purpose 
                    ? 'border-red-300 focus:border-red-500 bg-red-50' 
                    : 'border-gray-200 focus:border-blue-400 hover:border-blue-300'
                }`}
                required
              >
                <option value="">選択してください</option>
                <option value="一人食事">一人食事</option>
                <option value="友人・知人との食事">友人・知人との食事</option>
                <option value="家族での食事">家族での食事</option>
                <option value="デート">デート</option>
                <option value="会社・仕事関係">会社・仕事関係</option>
                <option value="お祝い・記念日">お祝い・記念日</option>
                <option value="その他">その他</option>
              </select>
              {errors.visit_purpose && (
                <p className="text-red-500 text-sm mt-2 text-center">{errors.visit_purpose}</p>
              )}
            </div>

            {/* 利用時間帯 */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md border border-gray-200">
              <label className="text-lg font-semibold text-gray-800 mb-4 block">
                利用時間帯
                <span className="text-red-500 ml-2">*</span>
              </label>
              <select
                value={formData.time_period}
                onChange={(e) => updateField('time_period', e.target.value)}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-200 ${
                  errors.time_period 
                    ? 'border-red-300 focus:border-red-500 bg-red-50' 
                    : 'border-gray-200 focus:border-blue-400 hover:border-blue-300'
                }`}
                required
              >
                <option value="">選択してください</option>
                <option value="モーニング">モーニング</option>
                <option value="ランチ">ランチ</option>
                <option value="ディナー">ディナー</option>
              </select>
              {errors.time_period && (
                <p className="text-red-500 text-sm mt-2 text-center">{errors.time_period}</p>
              )}
            </div>

            {/* 一人当たりの予算 */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md border border-gray-200">
              <label className="text-lg font-semibold text-gray-800 mb-4 block">
                一人当たりの予算
                <span className="text-sm text-gray-500 ml-2">(任意)</span>
              </label>
              <select
                value={formData.budget_per_person}
                onChange={(e) => updateField('budget_per_person', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-400 hover:border-blue-300 transition-all duration-200"
              >
                <option value="">選択してください</option>
                <option value="〜¥999">〜¥999</option>
                <option value="¥1,000〜¥1,999">¥1,000〜¥1,999</option>
                <option value="¥2,000〜¥2,999">¥2,000〜¥2,999</option>
                <option value="¥3,000〜¥3,999">¥3,000〜¥3,999</option>
                <option value="¥4,000〜¥4,999">¥4,000〜¥4,999</option>
                <option value="¥5,000〜¥5,999">¥5,000〜¥5,999</option>
                <option value="¥6,000〜¥7,999">¥6,000〜¥7,999</option>
                <option value="¥8,000〜¥9,999">¥8,000〜¥9,999</option>
                <option value="¥10,000〜¥14,999">¥10,000〜¥14,999</option>
                <option value="¥15,000〜¥19,999">¥15,000〜¥19,999</option>
                <option value="¥20,000〜¥29,999">¥20,000〜¥29,999</option>
                <option value="¥30,000〜">¥30,000〜</option>
              </select>
            </div>
          </div>
        </div>

        {/* 料理評価セクション */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg border-2 border-green-200">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">料理について</h2>
            <p className="text-gray-600">お料理の評価をお聞かせください</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 料理評価 */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md border border-gray-200">
              <StarRating
                rating={formData.food_rating}
                onRatingChange={(rating) => updateField('food_rating', rating)}
                label="料理の評価"
                required
              />
              {errors.food_rating && (
                <p className="text-red-500 text-sm text-center mt-2">{errors.food_rating}</p>
              )}
            </div>

            {/* おいしかった料理 */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md border border-gray-200">
              <label className="text-lg font-semibold text-gray-800 mb-4 block">
                特においしかった料理
                <span className="text-sm text-gray-500 ml-2">(任意)</span>
              </label>
              <textarea
                value={formData.good_dishes}
                onChange={(e) => updateField('good_dishes', e.target.value)}
                rows={5}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-green-400 hover:border-green-300 transition-all duration-200 resize-none"
                placeholder="特においしかった料理名や印象に残った味などをお聞かせください"
              />
            </div>
          </div>
        </div>

        {/* 接客評価セクション */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg border-2 border-purple-200">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">接客について</h2>
            <p className="text-gray-600">スタッフの接客についてお聞かせください</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 接客評価 */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md border border-gray-200">
              <StarRating
                rating={formData.service_rating}
                onRatingChange={(rating) => updateField('service_rating', rating)}
                label="接客の評価"
                required
              />
              {errors.service_rating && (
                <p className="text-red-500 text-sm text-center mt-2">{errors.service_rating}</p>
              )}
            </div>

            {/* 接客の特筆事項 */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md border border-gray-200">
              <label className="text-lg font-semibold text-gray-800 mb-4 block">
                接客の特筆事項
                <span className="text-sm text-gray-500 ml-2">(任意)</span>
              </label>
              <textarea
                value={formData.service_notes}
                onChange={(e) => updateField('service_notes', e.target.value)}
                rows={5}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 hover:border-purple-300 transition-all duration-200 resize-none"
                placeholder="スタッフの対応で印象に残ったことがあればお聞かせください"
              />
            </div>
          </div>
        </div>

        {/* 雰囲気評価セクション */}
        <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg border-2 border-orange-200">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">お店の雰囲気について</h2>
            <p className="text-gray-600">店内の雰囲気についてお聞かせください</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 雰囲気評価 */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md border border-gray-200">
              <StarRating
                rating={formData.atmosphere_rating}
                onRatingChange={(rating) => updateField('atmosphere_rating', rating)}
                label="雰囲気の評価"
                required
              />
              {errors.atmosphere_rating && (
                <p className="text-red-500 text-sm text-center mt-2">{errors.atmosphere_rating}</p>
              )}
            </div>

            {/* 雰囲気の特筆事項 */}
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md border border-gray-200">
              <label className="text-lg font-semibold text-gray-800 mb-4 block">
                雰囲気の特筆事項
                <span className="text-sm text-gray-500 ml-2">(任意)</span>
              </label>
              <textarea
                value={formData.atmosphere_notes}
                onChange={(e) => updateField('atmosphere_notes', e.target.value)}
                rows={5}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-400 hover:border-orange-300 transition-all duration-200 resize-none"
                placeholder="店内の雰囲気や居心地の良さについてお聞かせください"
              />
            </div>
          </div>
        </div>

        {/* ご意見・ご感想セクション */}
        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg border-2 border-teal-200">
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-2">ご意見・ご感想</h2>
            <p className="text-gray-600">その他、ご意見やご感想があればお聞かせください</p>
          </div>

          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md border border-gray-200">
            <label className="text-lg font-semibold text-gray-800 mb-4 block">
              ご意見・ご感想
              <span className="text-sm text-gray-500 ml-2">(任意)</span>
            </label>
            <textarea
              value={formData.general_feedback}
              onChange={(e) => updateField('general_feedback', e.target.value)}
              rows={6}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-teal-400 hover:border-teal-300 transition-all duration-200 resize-none"
              placeholder="全体的なご感想や、改善点、次回の期待などがあればお聞かせください"
            />
          </div>
        </div>

        {/* 送信ボタンセクション */}
        <div className="text-center space-y-6">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full max-w-md mx-auto px-8 py-4 rounded-2xl font-bold text-lg transition-all duration-300 transform ${
              isSubmitting
                ? 'bg-gray-400 cursor-not-allowed text-white'
                : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover:scale-105 shadow-lg hover:shadow-xl text-white'
            }`}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                AI が口コミ下書きを生成中...
              </span>
            ) : (
              <span className="flex items-center justify-center">
                🚀 口コミ下書きを生成する
              </span>
            )}
          </button>

          <div className="max-w-2xl mx-auto bg-gray-50 rounded-xl p-6 shadow-md border border-gray-300">
            <div className="flex items-center justify-center mb-3">
              <h3 className="font-semibold text-gray-800">プライバシー保護について</h3>
            </div>
            <div className="text-sm text-gray-600 space-y-2">
              <p>• このアンケートは匿名で処理され、{shopName}のサービス向上のために活用されます</p>
              <p>• 生成される口コミ下書きは参考用です。実際に投稿するかどうかはお客様の自由です</p>
              <p>• 個人情報は適切に管理され、第三者に提供されることはありません</p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SurveyForm;